import React, { useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import {
  Button,
  TextField,
  Box,
  Grid,
  CircularProgress,
  Typography,
  Paper
} from "@mui/material";
import { DataGrid } from '@mui/x-data-grid'; // Import DataGrid
import ButtonContained from "../../components/StyledComponents/ButtonContained";
import ButtonOutlined from "../../components/StyledComponents/ButtonOutlined";
import { Card, CardHeader, Dialog, Bar } from '@ui5/webcomponents-react';
import AppLayout from "../../components/AppLayout/AppLayout";
import httpRequest from "../../httpRequest";

const AuditsPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [audits, setAudits] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false); // Added loading state
  const [error, setError] = useState(null); // Added error state
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    fetchAudits();
  }, []);

  const fetchAudits = async () => {
    setLoading(true);
    setError(null); // Reset error state
    try {
      const response = await httpRequest.get(`${process.env.REACT_APP_BACKEND_URL}/audit`);
      setAudits(response.data.audits);
    } catch (error) {
      setError("Error fetching audits");
      enqueueSnackbar("Error fetching audits", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const clearAudits = async () => {
    if (!startDate || !endDate) {
      enqueueSnackbar("Please provide both start date and end date", { variant: "warning" });
      return;
    }

    const formattedStartDate = `${startDate} 00:00:00`;
    const formattedEndDate = `${endDate} 23:59:59`;

    try {
      await httpRequest.delete(`${process.env.REACT_APP_BACKEND_URL}/audit/clear`, {
        params: { start_date: formattedStartDate, end_date: formattedEndDate },
      });
      enqueueSnackbar("Audits cleared successfully", { variant: "success" });
      fetchAudits(); // Refresh the audits after clearing
    } catch (error) {
      enqueueSnackbar("Error clearing audits", { variant: "error" });
    }
  };

  const handleOpenDialog = () => {
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setStartDate(""); // Clear input values
    setEndDate("");   // Clear input values
  };

  const filteredAudits = audits.filter((audit) => {
    const lowerCaseQuery = searchQuery.toLowerCase();
    return (
      (audit.user_name && audit.user_name.toLowerCase().includes(lowerCaseQuery)) ||
      (audit.action_type && audit.action_type.toLowerCase().includes(lowerCaseQuery)) ||
      (audit.ip_address && audit.ip_address.toLowerCase().includes(lowerCaseQuery)) ||
      (audit.continent && audit.continent.toLowerCase().includes(lowerCaseQuery)) ||
      (audit.country && audit.country.toLowerCase().includes(lowerCaseQuery)) ||
      (audit.city && audit.city.toLowerCase().includes(lowerCaseQuery)) ||
      (audit.device_type && audit.device_type.toLowerCase().includes(lowerCaseQuery)) ||
      (audit.device_os && audit.device_os.toLowerCase().includes(lowerCaseQuery)) ||
      (audit.client && audit.client.toLowerCase().includes(lowerCaseQuery)) ||
      (audit.description && audit.description.toLowerCase().includes(lowerCaseQuery))
    );
  });

  // Columns configuration for DataGrid
  const columns = [
    { field: 'id', headerName: 'ID', width: 90 },
    { field: 'user_name', headerName: 'User Name', width: 150 },
    { field: 'action_type', headerName: 'Action Type', width: 150 },
    { field: 'timestamp', headerName: 'Timestamp', width: 200 },
    { field: 'ip_address', headerName: 'IP Address', width: 150 },
    { field: 'continent', headerName: 'Continent', width: 120 },
    { field: 'country', headerName: 'Country', width: 120 },
    { field: 'city', headerName: 'City', width: 120 },
    { field: 'device_type', headerName: 'Device Type', width: 150 },
    { field: 'device_os', headerName: 'Device OS', width: 150 },
    { field: 'client', headerName: 'Client', width: 150 },
    { field: 'description', headerName: 'Description', width: 200 },
  ];

  return (
    <AppLayout>
      <Dialog
        headerText={`Clear Audits`}
        open={dialogOpen}
        onAfterClose={handleCloseDialog}
        footer={
          <Bar design="Footer" endContent={
            <>
              <ButtonOutlined style={{ marginRight: 8 }} onClick={handleCloseDialog} color="primary">
                Cancel
              </ButtonOutlined>
              <ButtonContained onClick={clearAudits} color="primary">
                Clear
              </ButtonContained>
            </>
          } />
        }
      >
  <TextField
                      label="Start Date"
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      fullWidth
                      InputLabelProps={{
                        shrink: true,
                      }}
                      sx={{ marginBottom: 2 }}
                    />
                    <TextField
                      label="End Date"
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      fullWidth
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
      </Dialog>
      <Grid container justifyContent="center" style={{ marginTop: "20px" }}>
        <Grid item xs={11} md={11}>
          <Card
            header={
              <CardHeader
                titleText="Audit Records"
                subtitleText="Search and Manage Audit Entries"
              />
            }
          >
           
              <div style={{ padding: '20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <input
                  placeholder="Search Audits..."
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{ flex: "1", padding: "10px", marginRight: "20px" }}
                />
      
        
                  <ButtonOutlined onClick={handleOpenDialog}>
                    Clear Audits
                  </ButtonOutlined>

        </div>
        <div style={{ padding: '20px' }}>

                {loading ? (
                  <CircularProgress sx={{ marginBottom: '20px' }} />
                ) : error ? (
                  <Typography variant="h6" color="error">
                    {error}
                  </Typography>
                ) : (
                  <div style={{ width: '100%' }}>
                    <DataGrid
                      rows={filteredAudits}
                      columns={columns}
           
                      autoHeight
                      disableSelectionOnClick
                      getRowId={(row) => row.id} // Ensure unique row id
                    />
                  </div>
                )}
             </div>
          </Card>
        </Grid>
      </Grid>
    </AppLayout>
  );
};

export default AuditsPage;
